import { Ban, CheckCircle, ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp, Edit, KeyRound, MailWarning, Search, Trash2, X } from 'lucide-react'
import { Fragment, useCallback, useEffect, useState } from 'react'
import Layout from '../../components/Layout'
import AdminAuthEmailDialog from '../../components/admin/AdminAuthEmailDialog'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { SortableTableHeader } from '../../components/ui/sortable-table'
import { useNotification } from '../../contexts/NotificationContext'
import { roleVariant, userStatusVariant } from '../../lib/badgeVariants'
import { formatHumanDate } from '../../lib/dateUtils'
import {
  deleteUser,
  fetchAllUsers,
  updateUserEmailOptOut,
  updateUserProfileAdmin,
  updateUserRole,
  updateUserStatus,
} from '../../services/userService'

const PAGE_SIZE = 50

export default function UserManagement({ embedded = false }) {
  const Wrapper = embedded ? Fragment : Layout
  const notify = useNotification()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  // { user, mode } — mode is 'confirmation' or 'reset'.
  const [authEmailDialog, setAuthEmailDialog] = useState(null)
  const [selectedUsers, setSelectedUsers] = useState(new Set())
  const [users, setUsers] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(0)
  const [sortConfig, setSortConfig] = useState({ key: 'full_name', direction: 'asc' })
  const [, setLoading] = useState(true)
  const [editForm, setEditForm] = useState({
    full_name: '',
    email: '',
    role: '',
    status: '',
    email_opt_out: false
  })

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true)
      const data = await fetchAllUsers({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        search: searchTerm.trim() || null,
        role: filterRole === 'all' ? null : filterRole,
        status: filterStatus === 'all' ? null : filterStatus,
        sortColumn: sortConfig.key,
        sortDirection: sortConfig.direction,
      })
      setUsers(data.users || [])
      setTotalCount(Number(data.total_count) || 0)
    } catch (err) {
      console.error('Error fetching users:', err)
    } finally {
      setLoading(false)
    }
  }, [page, searchTerm, filterRole, filterStatus, sortConfig.key, sortConfig.direction])

  // Debounce search input — wait 300ms after typing stops, then refetch from page 0.
  useEffect(() => {
    const timer = setTimeout(() => { setPage(0) }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset to page 0 whenever a filter/sort changes (search resets page via the debounce above).
  useEffect(() => { setPage(0) }, [filterRole, filterStatus, sortConfig.key, sortConfig.direction])

  useEffect(() => {
    fetchUsers()
    const handleVisibilityChange = () => { if (!document.hidden) fetchUsers() }
    const handleFocus = () => fetchUsers()
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [fetchUsers])

  // Server-side filtered/sorted/paginated. The local `users` array is already the page slice.
  const sortedData = users
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const direction = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
      return { key, direction }
    })
  }

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return <ChevronsUpDown className="h-4 w-4 inline ml-1 text-muted-foreground" />
    }
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="h-4 w-4 inline ml-1" />
      : <ChevronDown className="h-4 w-4 inline ml-1" />
  }

  const lastActiveTitle = (user) => {
    const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : 'never')
    return `Last active: ${fmt(user.lastActiveAt || user.lastSignIn)}\nLast sign-in with password/link: ${fmt(user.lastSignIn)}`
  }

  const getRoleBadge = (role) => {
    return <Badge variant={roleVariant[role] || 'secondary'}>{role}</Badge>
  }

  const getStatusBadge = (status) => {
    return <Badge variant={userStatusVariant[status] || 'muted'}>{status === 'active' ? 'Active' : 'Suspended'}</Badge>
  }

  const handleEditClick = (user) => {
    setEditingUser(user)
    setEditForm({
      full_name: user.full_name || '',
      email: user.email,
      role: user.role,
      status: user.status,
      email_opt_out: !!user.email_opt_out
    })
    setShowEditModal(true)
  }

  const handleSaveEdit = async () => {
    try {
      let hasChanges = false

      // Parse full name into first and last name
      const nameParts = (editForm.full_name || '').trim().split(/\s+/)
      const firstName = nameParts[0] || ''
      const lastName = nameParts.slice(1).join(' ') || ''
      
      const originalFirstName = editingUser.first_name || ''
      const originalLastName = editingUser.last_name || ''

      // Update profile (name and/or email) if changed
      if (firstName !== originalFirstName || lastName !== originalLastName || editForm.email !== editingUser.email) {
        await updateUserProfileAdmin({
          user_id_param: editingUser.id,
          new_first_name: firstName !== originalFirstName ? firstName : null,
          new_last_name: lastName !== originalLastName ? lastName : null,
          new_email: editForm.email !== editingUser.email ? editForm.email : null
        })
        hasChanges = true
      }

      // Update role if changed
      if (editForm.role !== editingUser.role) {
        await updateUserRole({
          user_id_param: editingUser.id,
          new_role_param: editForm.role
        })
        hasChanges = true
      }

      // Update status if changed
      if (editForm.status !== editingUser.status) {
        await updateUserStatus({
          user_id_param: editingUser.id,
          new_status_param: editForm.status
        })
        hasChanges = true
      }

      // Update email opt-out if changed
      if (editForm.email_opt_out !== !!editingUser.email_opt_out) {
        await updateUserEmailOptOut({
          user_id_param: editingUser.id,
          new_opt_out_param: editForm.email_opt_out
        })
        hasChanges = true
      }

      if (hasChanges) {
        notify.success(`User "${editForm.full_name}" updated successfully!`)
        fetchUsers() // Refresh list
      } else {
        notify.info('No changes to save')
      }
      
      setShowEditModal(false)
      setEditingUser(null)
    } catch (err) {
      console.error('Error updating user:', err)
      notify.error(err.message || 'Failed to update user')
    }
  }

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'suspended' : 'active'
    try {
      await updateUserStatus({
        user_id_param: user.id,
        new_status_param: newStatus
      })
      notify.success(`User ${newStatus === 'active' ? 'activated' : 'suspended'} successfully!`)
      fetchUsers()
    } catch (err) {
      console.error('Error updating status:', err)
      notify.error(err.message || 'Failed to update status')
    }
  }

  const handleDeleteUser = async () => {
    const confirmed = await notify.confirm({
      title: 'Delete User',
      description: `Are you sure you want to delete user "${editForm.full_name || editForm.email}"? This action cannot be undone.`,
      variant: 'destructive',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    })
    
    if (!confirmed) return

    try {
      const data = await deleteUser({
        user_id_param: editingUser.id
      })

      if (!data.success) throw new Error(data.error)

      notify.success(`User "${editForm.full_name || data.deleted_email}" deleted successfully!`)
      setShowEditModal(false)
      setEditingUser(null)
      fetchUsers()
    } catch (err) {
      console.error('Error deleting user:', err)
      notify.error(err.message || 'Failed to delete user')
    }
  }

  const handleCancelEdit = () => {
    setShowEditModal(false)
    setEditingUser(null)
    setEditForm({
      full_name: '',
      email: '',
      role: '',
      status: ''
    })
  }

  // Bulk selection handlers
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev)
      if (newSet.has(userId)) {
        newSet.delete(userId)
      } else {
        newSet.add(userId)
      }
      return newSet
    })
  }

  const selectAllUsers = () => {
    if (selectedUsers.size === sortedData.length) {
      setSelectedUsers(new Set())
    } else {
      setSelectedUsers(new Set(sortedData.map(u => u.id)))
    }
  }

  // Bulk actions
  const handleBulkChangeRole = async (newRole) => {
    if (selectedUsers.size === 0) return
    
    const confirmed = await notify.confirm({
      title: 'Change Role',
      description: `Change role to "${newRole}" for ${selectedUsers.size} selected user(s)?`,
      confirmText: 'Change Role',
      cancelText: 'Cancel'
    })
    
    if (confirmed) {
      try {
        let successCount = 0
        let failCount = 0

        // Update role for each selected user
        for (const userId of selectedUsers) {
          try {
            await updateUserRole({
              user_id_param: userId,
              new_role_param: newRole
            })
            successCount++
          } catch (err) {
            console.error(`Error updating role for user ${userId}:`, err)
            failCount++
          }
        }

        // Show results
        if (successCount > 0) {
          notify.success(`${successCount} user(s) updated to role: ${newRole}`)
        }
        if (failCount > 0) {
          notify.error(`Failed to update ${failCount} user(s)`)
        }

        // Clear selection and refresh
        setSelectedUsers(new Set())
        fetchUsers()
      } catch (err) {
        console.error('Error in bulk role change:', err)
        notify.error('Failed to update user roles')
      }
    }
  }

  const handleBulkChangeStatus = async (newStatus) => {
    if (selectedUsers.size === 0) return
    
    const action = newStatus === 'active' ? 'Activate' : 'Suspend'
    const confirmed = await notify.confirm({
      title: `${action} Users`,
      description: `${action} ${selectedUsers.size} selected user(s)?`,
      confirmText: action,
      cancelText: 'Cancel'
    })
    
    if (confirmed) {
      try {
        let successCount = 0
        let failCount = 0

        // Update status for each selected user
        for (const userId of selectedUsers) {
          try {
            await updateUserStatus({
              user_id_param: userId,
              new_status_param: newStatus
            })
            successCount++
          } catch (err) {
            console.error(`Error updating status for user ${userId}:`, err)
            failCount++
          }
        }

        // Show results
        if (successCount > 0) {
          notify.success(`${successCount} user(s) ${newStatus === 'active' ? 'activated' : 'suspended'}`)
        }
        if (failCount > 0) {
          notify.error(`Failed to update ${failCount} user(s)`)
        }

        // Clear selection and refresh
        setSelectedUsers(new Set())
        fetchUsers()
      } catch (err) {
        console.error('Error in bulk status change:', err)
        notify.error('Failed to update user status')
      }
    }
  }

  const handleBulkDelete = async () => {
    if (selectedUsers.size === 0) return
    
    const confirmed = await notify.confirm({
      title: 'Delete Users',
      description: `Are you sure you want to delete ${selectedUsers.size} selected user(s)? This action cannot be undone.`,
      variant: 'destructive',
      requiresTyping: selectedUsers.size > 5,
      confirmationText: 'DELETE',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    })
    
    if (confirmed) {
      try {
        let successCount = 0
        let failCount = 0
        const errors = []

        // Delete each selected user
        for (const userId of selectedUsers) {
          try {
            const data = await deleteUser({
              user_id_param: userId
            })

            if (!data.success) throw new Error(data.error)

            successCount++
          } catch (err) {
            console.error(`Error deleting user ${userId}:`, err)
            failCount++
            errors.push(err.message)
          }
        }

        // Show results
        if (successCount > 0) {
          notify.success(`${successCount} user(s) deleted successfully`)
        }
        if (failCount > 0) {
          notify.error(`Failed to delete ${failCount} user(s)`)
        }

        // Clear selection and refresh
        setSelectedUsers(new Set())
        fetchUsers()
      } catch (err) {
        console.error('Error in bulk delete:', err)
        notify.error('Failed to delete users')
      }
    }
  }

  return (
    <Wrapper>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">User Management</h1>
          <p className="text-muted-foreground">Manage app users and permissions</p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-md"
                  />
                </div>
              </div>

              {/* Role Filter */}
              <div>
                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex items-center gap-2">Users <span className="text-muted-foreground font-normal">{totalCount}</span></CardTitle>
              {selectedUsers.size > 0 && (
                <Badge variant="solid-purple">{selectedUsers.size} selected</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Bulk Actions Bar */}
            {selectedUsers.size > 0 && (
              <div className="mb-4 p-4 bg-primary/10 rounded-lg">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <span className="font-medium">{selectedUsers.size} user(s) selected</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select onValueChange={(value) => handleBulkChangeRole(value)}>
                      <SelectTrigger className="w-[150px] text-sm">
                        <SelectValue placeholder="Change Role..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleBulkChangeStatus('suspended')}
                    >
                      <Ban className="h-4 w-4 mr-2" />
                      Suspend
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleBulkChangeStatus('active')}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Activate
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDelete}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 w-12">
                      <input
                        type="checkbox"
                        checked={selectedUsers.size === sortedData.length && sortedData.length > 0}
                        onChange={selectAllUsers}
                        className="rounded"
                      />
                    </th>
                    <SortableTableHeader 
                      onClick={() => handleSort('full_name')}
                      className="text-left py-3 px-4"
                    >
                      Name <SortIcon column="full_name" />
                    </SortableTableHeader>
                    <SortableTableHeader 
                      onClick={() => handleSort('email')}
                      className="text-left py-3 px-4"
                    >
                      Email <SortIcon column="email" />
                    </SortableTableHeader>
                    <SortableTableHeader 
                      onClick={() => handleSort('role')}
                      className="text-left py-3 px-4"
                    >
                      Role <SortIcon column="role" />
                    </SortableTableHeader>
                    <SortableTableHeader 
                      onClick={() => handleSort('status')}
                      className="text-left py-3 px-4"
                    >
                      Status <SortIcon column="status" />
                    </SortableTableHeader>
                    <SortableTableHeader 
                      onClick={() => handleSort('createdAt')}
                      className="text-left py-3 px-4"
                    >
                      Join Date <SortIcon column="createdAt" />
                    </SortableTableHeader>
                    <SortableTableHeader 
                      onClick={() => handleSort('lastActive')}
                      className="text-left py-3 px-4"
                    >
                      Last Active <SortIcon column="lastActive" />
                    </SortableTableHeader>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((user) => (
                    <tr key={user.id} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="py-3 px-4 font-medium">
                        {user.first_name || user.last_name 
                          ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                          : 'Not set'}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">{user.email}</td>
                      <td className="py-3 px-4">{getRoleBadge(user.role)}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap items-center gap-1">
                          {getStatusBadge(user.status)}
                          {/* Ticket c492ca99: an unconfirmed account looks
                              "Active" but can't log in at all. Call it out so
                              the resend action is discoverable. */}
                          {!user.emailConfirmedAt && (
                            <Badge variant="orange" title="Email never confirmed — this user can't log in">
                              Unconfirmed
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {formatHumanDate(user.createdAt)}
                      </td>
                      {/* Ticket 98466c80: last_sign_in_at only moves on a fresh
                          credential sign-in, never on a session refresh, so
                          players who stay logged in looked months stale.
                          lastActiveAt folds in session refreshes. */}
                      <td
                        className="py-3 px-4 text-sm text-muted-foreground"
                        title={lastActiveTitle(user)}
                      >
                        {(user.lastActiveAt || user.lastSignIn)
                          ? formatHumanDate(user.lastActiveAt || user.lastSignIn)
                          : 'Never'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(user)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {!user.emailConfirmedAt && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setAuthEmailDialog({ user, mode: 'confirmation' })}
                              title="Resend confirmation email"
                            >
                              <MailWarning className="h-4 w-4 text-[var(--orange-11)]" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setAuthEmailDialog({ user, mode: 'reset' })}
                            title="Send password reset email"
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          {user.status === 'active' ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                const confirmed = await notify.confirm({
                                  title: 'Suspend User',
                                  description: `Suspend ${user.full_name || user.email}?`,
                                  confirmText: 'Suspend',
                                  cancelText: 'Cancel'
                                })
                                if (confirmed) {
                                  handleToggleStatus(user)
                                }
                              }}
                              title="Suspend user"
                            >
                              <Ban className="h-4 w-4 text-destructive" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                const confirmed = await notify.confirm({
                                  title: 'Activate User',
                                  description: `Activate ${user.full_name || user.email}?`,
                                  confirmText: 'Activate',
                                  cancelText: 'Cancel'
                                })
                                if (confirmed) {
                                  handleToggleStatus(user)
                                }
                              }}
                              title="Activate user"
                            >
                              <CheckCircle className="h-4 w-4 text-[var(--color-success)]" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {totalCount === 0
                  ? 'No users'
                  : `Showing ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, totalCount)} of ${totalCount}`}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                </Button>
                <span className="text-muted-foreground">Page {page + 1} of {totalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {authEmailDialog && (
          <AdminAuthEmailDialog
            user={authEmailDialog.user}
            mode={authEmailDialog.mode}
            onClose={() => setAuthEmailDialog(null)}
          />
        )}

        {/* Edit User Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Edit User</CardTitle>
                  <Button variant="ghost" size="icon" onClick={handleCancelEdit}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Full Name *</label>
                    <Input
                      value={editForm.full_name}
                      onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                      placeholder="Enter full name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Email *</label>
                    <Input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      placeholder="user@example.com"
                    />
                    {/* Ticket c492ca99: confirmation state in full, with the
                        date. The table only flags the unconfirmed case. */}
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                      {editingUser?.emailConfirmedAt ? (
                        <span className="text-muted-foreground">
                          Email confirmed {formatHumanDate(editingUser.emailConfirmedAt)}
                        </span>
                      ) : (
                        <>
                          <Badge variant="orange">Never confirmed</Badge>
                          <span className="text-muted-foreground">This user can't log in until they confirm.</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setAuthEmailDialog({ user: editingUser, mode: 'confirmation' })}
                          >
                            <MailWarning className="h-4 w-4 mr-1" />
                            Resend
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Role */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Role *</label>
                    <Select value={editForm.role || undefined} onValueChange={(value) => setEditForm({ ...editForm, role: value })}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Status *</label>
                    <Select value={editForm.status || undefined} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Email opt-out (owner request 2026-06-06): drives the
                      email list of users who have NOT opted out. */}
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={editForm.email_opt_out}
                      onChange={(e) => setEditForm({ ...editForm, email_opt_out: e.target.checked })}
                    />
                    Opt-out of email notifications
                  </label>

                  {/* Action Buttons */}
                  {(!editForm.full_name || !editForm.email || !editForm.role || !editForm.status) && (
                    <div className="p-2 bg-[var(--orange-3)] border border-[var(--orange-6)] rounded-lg">
                      <p className="text-xs text-[var(--orange-12)]">
                        Please fill in all required fields: name, email, role, and status
                      </p>
                    </div>
                  )}

                  <div className="flex flex-col gap-2 pt-4 mt-4 border-t sm:flex-row">
                    <Button
                      variant="destructive"
                      onClick={handleDeleteUser}
                      className="sm:order-1"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete User
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleCancelEdit}
                      className="flex-1 sm:order-2"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveEdit}
                      className="flex-1 sm:order-3"
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Wrapper>
  )
}
